import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, X, CheckCircle, AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";

interface Suggestion {
  id: string;
  type: 'incomplete' | 'context' | 'terminology' | 'structure';
  message: string;
  suggestion: string;
  position?: number;
}

interface RealtimeSuggestionsProps {
  content: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  onApplySuggestion?: (suggestion: string) => void;
}

export const RealtimeSuggestions = ({
  content,
  enabled,
  onToggle,
  onApplySuggestion,
}: RealtimeSuggestionsProps) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const analyzeContent = useCallback((text: string): Suggestion[] => {
    const newSuggestions: Suggestion[] = [];
    let idCounter = 0;

    // Check for incomplete sentences
    const sentences = text.split(/[.!?]+/);
    sentences.forEach((sentence, index) => {
      if (sentence.trim() && sentence.trim().split(' ').length < 3) {
        newSuggestions.push({
          id: `incomplete-${idCounter++}`,
          type: 'incomplete',
          message: 'Incomplete sentence detected',
          suggestion: `Consider expanding: "${sentence.trim()}"`,
        });
      }
    });

    // Check for missing medical context
    const medicalTerms = text.match(/\b[A-Z]{2,}\b/g);
    if (medicalTerms && medicalTerms.length > 3) {
      const uniqueTerms = [...new Set(medicalTerms)];
      if (uniqueTerms.length > 5) {
        newSuggestions.push({
          id: `context-${idCounter++}`,
          type: 'context',
          message: 'Multiple medical terms without context',
          suggestion: 'Consider adding explanations for abbreviations',
        });
      }
    }

    // Check for better word choices
    const verbosePatterns = [
      { pattern: /in order to/gi, suggestion: 'Replace "in order to" with "to"' },
      { pattern: /due to the fact that/gi, suggestion: 'Replace "due to the fact that" with "because"' },
      { pattern: /at this point in time/gi, suggestion: 'Replace "at this point in time" with "now"' },
    ];

    verbosePatterns.forEach(({ pattern, suggestion }) => {
      if (pattern.test(text)) {
        newSuggestions.push({
          id: `terminology-${idCounter++}`,
          type: 'terminology',
          message: 'Verbose phrasing detected',
          suggestion,
        });
      }
    });

    // Check for structure improvements
    if (text.length > 500 && !/^#{1,3}\s+/m.test(text)) {
      newSuggestions.push({
        id: `structure-${idCounter++}`,
        type: 'structure',
        message: 'Long content without headings',
        suggestion: 'Consider adding section headings for better organization',
      });
    }

    return newSuggestions.slice(0, 5); // Limit to 5 suggestions
  }, []);

  useEffect(() => {
    if (!enabled || !content) {
      setSuggestions([]);
      return;
    }

    // Debounce the analysis
    const timer = setTimeout(() => {
      const newSuggestions = analyzeContent(content);
      setSuggestions(newSuggestions);
    }, 1000);

    return () => clearTimeout(timer);
  }, [content, enabled, analyzeContent]);

  const getTypeIcon = (type: Suggestion['type']) => {
    switch (type) {
      case 'incomplete': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'context': return <Lightbulb className="h-4 w-4 text-blue-500" />;
      case 'terminology': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'structure': return <AlertCircle className="h-4 w-4 text-orange-500" />;
    }
  };

  const getTypeColor = (type: Suggestion['type']) => {
    switch (type) {
      case 'incomplete': return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
      case 'context': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'terminology': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'structure': return 'bg-orange-500/10 text-orange-700 dark:text-orange-400';
    }
  };

  const dismissSuggestion = (id: string) => {
    setSuggestions(prev => prev.filter(s => s.id !== id));
  };

  if (!enabled) {
    return (
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
              Real-time Suggestions
            </span>
            <Switch checked={enabled} onCheckedChange={onToggle} />
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-primary" />
            Real-time Suggestions ({suggestions.length})
          </span>
          <Switch checked={enabled} onCheckedChange={onToggle} />
        </CardTitle>
      </CardHeader>
      {suggestions.length > 0 && (
        <CardContent>
          <ScrollArea className="h-[200px] pr-3">
            <div className="space-y-2">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="border rounded-lg p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      {getTypeIcon(suggestion.type)}
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getTypeColor(suggestion.type)} variant="outline">
                            {suggestion.type}
                          </Badge>
                          <p className="text-xs font-medium">{suggestion.message}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{suggestion.suggestion}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissSuggestion(suggestion.id)}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  );
};
